import type { Time } from "@suse-o11y/date-time/time";
import { Dialog, DialogClose, DialogTitle } from "@suse-o11y/dialog";
import { findTag } from "@suse-o11y/find-tag";
import { IconClose } from "@suse-o11y/icons";
import { Signal, useMonitor } from "@suse-o11y/signal";
import { usePermanent } from "@suse-o11y/use-permanent";
import { useScrollPagination } from "@suse-o11y/use-scroll-pagination";
import isEmpty from "lodash/isEmpty";
import React, { useState } from "react";
import { useMeasure } from "react-use";

import type { TimeRange } from "../../../../../../app/state/view/timeline/TimeRange";
import { Api, type FullComponent, type LogSeverity } from "../../../../../../common/openapi";
import { DEFAULT_DROPDOWN_FILTER, type DropdownFilterItem } from "../../../../../../components/_dropdown-filter";
import { HistogramChart } from "../../../../../../components/_histogram-chart";
import { ScrollingContainer } from "../../../../../../components/_scrolling-container";
import { Widget } from "../../../../../../components/_widget";
import { NotFound } from "../../../../../../components/react/not-found/NotFound";
import { RelativeContainer } from "../../../../../../components/react/RelativeContainer";
import { StateComponent } from "../../../../../../components/react/states/StateComponent";
import { getBucketCount } from "../../../../../../components/react/timeline/TimelineUtils";
import { getPodUidFromComponent } from "../../../../../../generic/get-pod-uid-from-component";
import {
	useInfinitiveQueryPodLogs,
	useQueryPodLogsHistogram,
	useRemoveQueriesPodLogs,
} from "../../../../../../queries/podLogs";

import { SEVERITY_ITEMS } from "./_element-properties-logs-drawer-filter-constants";
import { LOGS_DRAWER_QUERY_NAMESPACE } from "./_query-namespace";
import { ElementPropertiesLogsDrawerFilters, type FilterState } from "./ElementPropertiesLogsDrawerFilters";
import { ElementPropertiesLogsDrawerTable } from "./ElementPropertiesLogsDrawerTable";

const getValueFromDropdownFilterItem = (selectedContainer: DropdownFilterItem) =>
	isEmpty(selectedContainer.value) ? undefined : [selectedContainer.value];

interface ElementPropertiesLogsDrawerProps extends Pick<DrawerContentProps, "timestamp" | "timeRange" | "severity"> {
	component: FullComponent;
}

export const ElementPropertiesLogsDrawer: React.FC<ElementPropertiesLogsDrawerProps> = ({
	component,
	timestamp,
	timeRange,
	severity,
}) => {
	const podUid = getPodUidFromComponent(component);
	const cluster = findTag(component.data.tags, "cluster-name");

	if (podUid == null || cluster == null) {
		return null;
	}

	return (
		<Dialog className="drawer/dialog drawer-wide">
			<RelativeContainer className="flex min-h-0 flex-1 flex-col">
				<DrawerContent
					cluster={cluster}
					podUid={podUid}
					severity={severity}
					timeRange={timeRange}
					timestamp={timestamp}
					title={<StateComponent component={component} displayName />}
				/>
			</RelativeContainer>
		</Dialog>
	);
};

interface DrawerContentProps {
	cluster: string;
	podUid: string;
	title: React.ReactNode;
	timestamp: undefined | Time;
	timeRange: TimeRange;
	severity?: LogSeverity;
}

const DrawerContent: React.FC<DrawerContentProps> = ({
	cluster,
	podUid,
	title,
	timestamp,
	timeRange: initialTimeRange,
	severity,
}) => {
	const monitor = useMonitor();
	const tableContainerParentRef = React.useRef<HTMLDivElement>(null);
	const [order, setOrder] = useState<Api.LogsDirection>(Api.LogsDirection.OLDEST);
	const timeRange = usePermanent(() => Signal(initialTimeRange));
	const filters = usePermanent<FilterState>(() => ({
		search: Signal<string>(),
		severity: Signal(SEVERITY_ITEMS.find(({ value }) => value === severity?.toString()) ?? DEFAULT_DROPDOWN_FILTER),
		container: Signal(DEFAULT_DROPDOWN_FILTER),
	}));

	// width is calculated from the content box, meaning the border and the padding will not be included
	const [measureRef, { width }] = useMeasure<HTMLDivElement>();
	const bucketsCount = width > 0 ? getBucketCount(width) : 0;

	const resetQuery = useRemoveQueriesPodLogs({
		cluster,
		podUid,
		direction: order,
		containerNames: getValueFromDropdownFilterItem(filters.container.read(monitor))?.toString(),
		severityList: getValueFromDropdownFilterItem(filters.severity.read(monitor))?.toString(),
		query: filters.search.read(monitor),
	});

	const { data: buckets, isError: isBucketsError } = useQueryPodLogsHistogram(
		{
			cluster,
			podUID: podUid,
			query: filters.search.read(monitor),
			timestamp,
			timeRange: timeRange.read(monitor),
			containerNames: getValueFromDropdownFilterItem(filters.container.read(monitor)),
			severityList: getValueFromDropdownFilterItem(filters.severity.read(monitor)) as Array<Api.LogSeverity>,
			bucketsCount,
		},
		{
			enabled: bucketsCount > 0,
			namespace: LOGS_DRAWER_QUERY_NAMESPACE,
			refetchOnReconnect: false,
			refetchOnWindowFocus: false,
		},
	);

	const { data, fetchNextPage, isLoading, isFetching, isFetchingNextPage, hasNextPage, isError, error, refetch } =
		useInfinitiveQueryPodLogs(
			{
				cluster,
				podUid,
				query: filters.search.read(monitor),
				direction: order,
				containerNames: getValueFromDropdownFilterItem(filters.container.read(monitor)),
				timestamp,
				timeRange: timeRange.read(monitor),
				severityList: getValueFromDropdownFilterItem(filters.severity.read(monitor)) as Array<Api.LogSeverity>,
			},
			{
				namespace: LOGS_DRAWER_QUERY_NAMESPACE,
				refetchOnReconnect: false,
				refetchOnWindowFocus: false,
			},
		);

	const totalDBRowCount = data?.pages.at(0)?.matchesTotal ?? 0;

	useScrollPagination({
		scrollContainer: tableContainerParentRef.current,
		threshold: 300,
		enabled: !isFetching && hasNextPage,
		fetchNextPage,
		resetQuery,
	});

	// We must flatten the array of arrays from the useInfiniteQuery hook
	const flatData = React.useMemo(() => data?.pages.flatMap((page) => page.logs) ?? [], [data?.pages]);

	return (
		<>
			<header className="drawer-header">
				<DialogTitle className="drawer-title">{title}</DialogTitle>

				<DialogClose aria-label="Close" className="button button-square ml-auto">
					<IconClose className="size-3" />
				</DialogClose>
			</header>

			<ScrollingContainer className="drawer-body space-y-4" ref={tableContainerParentRef}>
				<ElementPropertiesLogsDrawerFilters
					cluster={cluster}
					filterState={filters}
					isFetching={isFetching}
					podUid={podUid}
					timeRange={timeRange}
					timestamp={timestamp}
					totalDBRowCount={totalDBRowCount}
				/>
				{totalDBRowCount > 0 && (
					<Widget card className="px-10 pt-5 pb-2">
						<HistogramChart buckets={isBucketsError ? [] : buckets} ref={measureRef} timeRange={timeRange} />
					</Widget>
				)}
				<ElementPropertiesLogsDrawerTable
					data={flatData}
					error={error}
					hasNextPage={hasNextPage ?? false}
					isError={isError}
					isFetching={isFetching}
					isFetchingNextPage={isFetchingNextPage}
					isLoading={isLoading}
					order={order}
					refetch={() => void refetch()}
					setOrder={setOrder}
					totalDBRowCount={totalDBRowCount}
				/>
				{!(isFetching || isError) && totalDBRowCount === 0 && <LogsEmptyState />}
			</ScrollingContainer>
		</>
	);
};

const LogsEmptyState: React.FC = () => (
	/*container height minus filter height*/
	<div className="h-[calc(100%-80px)]">
		<NotFound data-testid="no-element" header="No log entries found">
			We didn't find any log entries to match your selection.
			<br />
			{/*//TODO: add proper slug, and uncomment next lines*/}
			{/*You can go to our*/}
			{/*<DocLink slug="">documentation site</DocLink> to learn more about logs.*/}
			{/*<br />*/}
			If you already have logs, you can try to reduce the number of filters or change the time window.
		</NotFound>
	</div>
);
